import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth.store';

const TIMEOUT_MS = 30 * 60 * 1000;   // 30 minutes inactivity → logout
const WARN_BEFORE_MS = 2 * 60 * 1000; // show warning 2 min before

export interface AutoLogoutState {
  showWarning: boolean;
  secondsLeft: number;
  extendSession: () => void;
}

export function useAutoLogout(): AutoLogoutState {
  const { logout, isAuthenticated } = useAuthStore();
  const lastActivityRef = useRef<number>(Date.now());
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(120);

  const extendSession = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
  }, []);

  // Track any user interaction as "activity"
  useEffect(() => {
    if (!isAuthenticated) return;

    const onActivity = () => {
      lastActivityRef.current = Date.now();
      setShowWarning(false);
    };

    const events = ['mousedown', 'keydown', 'scroll', 'click', 'touchstart'] as const;
    events.forEach(e => document.addEventListener(e, onActivity, true));
    return () => events.forEach(e => document.removeEventListener(e, onActivity, true));
  }, [isAuthenticated]);

  // Periodic check every 10 s
  useEffect(() => {
    if (!isAuthenticated) return;

    const timer = setInterval(() => {
      const idle = Date.now() - lastActivityRef.current;

      if (idle >= TIMEOUT_MS) {
        clearInterval(timer);
        setShowWarning(false);
        logout();
        return;
      }

      if (idle >= TIMEOUT_MS - WARN_BEFORE_MS) {
        const left = Math.max(0, Math.ceil((TIMEOUT_MS - idle) / 1000));
        setSecondsLeft(left);
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }
    }, 10_000);

    return () => clearInterval(timer);
  }, [isAuthenticated, logout]);

  return { showWarning, secondsLeft, extendSession };
}
